// 创建微信预约
import { useContext, useEffect, useMemo } from "react"
import moment from "moment"
import { CalendarContext } from "../context"
import { useAssign, useInputChange } from "Hooks/basic"
import { AppContext } from "Views/Home/context"
import { getUserLocalInfo } from "Methods/user"
import { generateModalTimeStr } from "Methods/board"
import { Board, BoardModifyDialog } from "Types/board"
import { timeFormatStr } from "Methods/basic"
import request from "Methods/request"
import { InitialModal } from "Methods/modal"
import { checkUserPrivilege } from "Methods/privilege"
import config from "./config"
// 微信预约，文章类型选择器候选项过滤
export function useCreateStaticArticleType() {
    const { user } = useContext(AppContext)
    return useMemo(() => {
        const { department } = user
        const { type_name = "" } = department || {}
        let staticArticleType = []
        if (config.hasOwnProperty(type_name)) {
            staticArticleType = config[type_name]
        }
        if (config.hasOwnProperty(user.uid)) {
            staticArticleType = config[user.uid]
        }
        return staticArticleType
    }, [user])
}
// 微信预约，额外项
export function useWeChatExternals(board: Board | null) {
    const initialExternal = {
        articleTypeId: 0,
        author: {
            authorId: 0,
            principalId: "",
        },
    }
    const {
        object: external,
        handleChange: setExternals,
    } = useAssign(initialExternal)
    const resetExternal = () => setExternals(initialExternal)
    const { roleMap } = useContext(AppContext)
    const { isUser, isSideEditor } = roleMap
    useEffect(() => {
        if (board) {
            const articleTypeId = board.article_type === null || board.article_type.hidden ? 0 : board.article_type_id
            const authorId = board.author === null ? 0 : board.author_id
            const principalId = board.principal_id
            setExternals({
                articleTypeId,
                author: {
                    authorId,
                    principalId,
                },
            })
        } else if (isUser || isSideEditor) {
            // 点位运营和编辑部编辑的负责人为用户本身
            setExternals({
                author: {
                    authorId: 0,
                    principalId: getUserLocalInfo().uid,
                },
            })
        }
        // eslint-disable-next-line
    }, [board])
    const handleChangeAuthor = (author: { authorId: number, principalId: string }) => setExternals({ author })
    const handleChangeArticleType = (value: number) => setExternals({ articleTypeId: Number(value) })
    return {
        external,
        handleChangeAuthor,
        handleChangeArticleType,
        resetExternal,
    }
}
// 微信预约，弹窗
export function useCreateWeChat() {
    const {
        boardModifyDialog,
        handleChangeBoardModifyDialog,
        handleChangeSelectPlaceId,
        selectDateRange,
        handleSetSelectDateRange,
        handleSetEditMaterial,
    } = useContext(CalendarContext)
    const { handleChangeModal, privilegeMap, roleMap } = useContext(AppContext)
    const { update: materialUpdate = false } = checkUserPrivilege(privilegeMap, "material")
    const { isOpEditor, isUser, isBusiness } = roleMap
    const dialogData = boardModifyDialog as BoardModifyDialog
    const board = dialogData.board as Board
    const { isPass, isThreeDay } = dialogData
    const isEdit = board !== undefined
    const {
        external,
        handleChangeArticleType,
        handleChangeAuthor,
        resetExternal,
    } = useWeChatExternals(board)
    const { text, handleChange, clearText, setText } = useInputChange("")
    const { text: projectId, handleChange: handleProjectIdChange, clearText: clearProjectId, setText: setProjectId } = useInputChange("")
    const { text: projectMoney, handleChange: handleProjectMoneyChange, clearText: clearProjectMoney, setText: setProjectMoney } = useInputChange("")
    const staticArticleType = useCreateStaticArticleType()
    useEffect(() => {
        if (board) {
            setText(board.description)
            setProjectId(board.project_id)
            setProjectMoney(board.project_money)
        }
        // eslint-disable-next-line
    }, [board])
    const { modalTimeStr, adTimes } = useMemo(
        () => generateModalTimeStr(selectDateRange),
        [selectDateRange],
    )
    const { add, update } = useWeChatRequest()
    const place = dialogData.place
    const date = dialogData.date
    const callback = async () => {
        if (isEdit) {
            await update(
                board.id, {
                ad_time: board.ad_time,
                seq_id: board.seq_id,
                description: text,
                author_id: external.author.authorId,
                principal_id: external.author.principalId ? external.author.principalId : "",
                article_type_id: external.articleTypeId,
                is_draft: false,
                project_id: projectId || '',
                project_money: projectMoney || '',
            })
        } else {
            await add({
                description: text,
                seq_id: dialogData.place.id,
                ad_times: adTimes.length ? adTimes : [dialogData.date],
                article_type_id: external.articleTypeId,
                principal_id: external.author.principalId,
                author_id: external.author.authorId,
                is_draft: false,
                project_id: projectId || '',
                project_money: projectMoney || '',
            })
        }
        handleSetSelectDateRange(null)
        resetExternal()
    }
    const editCallback = async () => {
        const { data } = await request.get(`/apis/material/${board.material.id}`)
        handleSetEditMaterial({
            open: true,
            update: materialUpdate,
            isBom: isUser,
            modifyData: data,
            handleClose: () => {
                handleSetEditMaterial({
                    modifyData: null,
                    open: false,
                    update: false,
                    isBom: false,
                    handleClose: () => { },
                    handleConfirm: () => { },
                })
            },
            handleConfirm: async (newItem) => {
                try {
                    await request.put(`/apis/material/${newItem.id}/update`, newItem)
                    handleChangeModal({
                        show: true,
                        type: "success",
                        title: "修改成功！",
                        timeout: 800,
                        onConfirm: () => {
                            handleChangeModal(InitialModal)
                        },
                    })
                } catch (err) {
                    if (!navigator.onLine) {
                        handleChangeModal({
                            show: true,
                            type: "warning",
                            customButtons: [],
                            title: "oops! 断网啦！",
                            timeout: 3000,
                            onConfirm: () => {
                                handleChangeModal(InitialModal)
                            },
                        })
                        return
                    }
                    /* tslint:disable-next-line */
                    const { response: { data = {} } = {}, message } = err
                    handleChangeModal({
                        show: true,
                        type: "warning",
                        customButtons: [],
                        title: data.message || message,
                        timeout: 3000,
                        onConfirm: () => {
                            handleChangeModal(InitialModal)
                        },
                    })
                }
                handleSetEditMaterial({
                    modifyData: null,
                    open: false,
                    update: false,
                    isBom: false,
                    handleClose: () => { },
                    handleConfirm: () => { },
                })
            },
        })
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        handleSetSelectDateRange(null)
        clearText()
        resetExternal()
        handleChangeSelectPlaceId(0)
    }
    let dateStr
    if (isEdit) {
        dateStr = moment(board.ad_time).format(timeFormatStr.whole)
    } else {
        dateStr = moment(date).format(timeFormatStr.all) + place.name
    }
    const disabled = text === "" || external.articleTypeId === 0 || external.author.principalId === "" || (isBusiness && (!!(projectId && projectId.length > 50) || !!(projectMoney && projectMoney.length > 50) || projectId === ""))
    return {
        callback, cancelCallback,
        editCallback,
        dialogData, disabled,
        dateStr, modalTimeStr,
        text, handleChange,
        projectId, handleProjectIdChange,
        projectMoney, handleProjectMoneyChange,
        external, isBusiness, isPass, isThreeDay,
        handleChangeAuthor,
        handleChangeArticleType,
        staticArticleType,
    }
}

export interface WeChatRequestData {
    description: string,
    seq_id: number,
    article_type_id: number,
    principal_id: string,
    author_id: number,
    is_draft: boolean,
    project_id: string
    project_money: string
}
// 微信预约请求
export function useWeChatRequest() {
    interface AddData extends WeChatRequestData {
        ad_times: Date[],
    }
    interface UpdateData extends WeChatRequestData {
        ad_time: Date,
    }
    const add = async (data: AddData) => {
        try {
            await request.post(`/apis/wechat_boards`, data)
        } catch (e) {
            throw e
        }
    }
    const update = async (
        boardId: number = 0,
        data: UpdateData,
    ) => {
        try {
            await request.put(`/apis/wechat_board/${boardId}/update_time`, data)
        } catch (e) {
            throw e
        }
    }
    return { add, update }
}
